<script setup lang="ts">
import { i18n } from '@/i18n'
import { appVersion } from '@/constants'
import BlockSection from '@/components/cms/BlockSection.vue'
import BlockListSection from '@/components/cms/BlockListSection.vue'
import ProfileListSection from '@/components/cms/ProfileListSection.vue'
import ResourceListSection from '@/components/cms/ResourceListSection.vue'
import PageGroupSection from '@/components/cms/PageGroupSection.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import { StrapiComponentName, type Block, type BlockList, type Page } from '@/models'
import useStrapiStore from '@/composition/strapiStore'
import { computed, type PropType } from 'vue'
import { mainBackgroundClasses } from '@/constants'

const strapiStore = useStrapiStore()
const t = i18n.global.t

const props = defineProps({
  page: {
    type: Object as PropType<Page>,
    required: false,
  },
})

// Supply a page in the props, or if not it will retrieve the current page from the store
const thePage = computed(() => props.page ?? strapiStore.getters.currentPage.value)

const componentMap = {
  [StrapiComponentName.SimpleBlock]: BlockSection,
  [StrapiComponentName.Blocks]: BlockListSection,
  [StrapiComponentName.Profiles]: ProfileListSection,
  [StrapiComponentName.Resources]: ResourceListSection,
  [StrapiComponentName.PageGroups]: PageGroupSection,
} as const

const lastBackgroundColour = computed(() => {
  const lastIndex = (thePage.value?.sections.length ?? 0) - 1
  let bgColourName = 'none'
  if (thePage.value && lastIndex > -1) {
    const section = thePage.value.sections[lastIndex]!
    let block: Block
    if (section.__component === StrapiComponentName.SimpleBlock) block = section as Block
    else block = (section as BlockList).details
    if (block) bgColourName = block.backgroundColour ?? 'none'
  }
  return mainBackgroundClasses(bgColourName)
})

const ptRoutes = ['stories']
</script>

<template>
  <div
    v-if="thePage"
    class="relative"
    :class="[lastBackgroundColour, ptRoutes.includes(thePage.name) ? 'pt-30' : '']"
  >
    <p
      v-if="thePage.name === 'about'"
      class="font-alexandria text-[14px] absolute top-0 right-2 text-black"
    >
      v{{ appVersion }}
    </p>
    <component
      v-for="(section, i) of thePage.sections"
      :key="section.id"
      :is="componentMap[section.__component as keyof typeof componentMap]"
      :section="section"
      :isHeaderBlock="i === 0"
    />
    <SiteFooter class="mt-32" />
  </div>
  <div v-else class="flex flex-col items-center justify-center">
    <h1 class="text-2xl">{{ t('page-not-found') }}</h1>
  </div>
</template>

<style scoped></style>
